import { useState, useEffect } from 'react';
import { canvasToImageUrl, revokeImageUrl } from '../../../../helpers/canvasUtils';

interface BoundingBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

export const useCroppedImage = (
  imageUrl?: string,
  boundingBox?: BoundingBox
): { croppedImageUrl?: string } => {
  const [croppedImageUrl, setCroppedImageUrl] = useState<string | undefined>();

  useEffect(() => {
    if (!imageUrl || !boundingBox) {
      // Cleanup previous image URL if it exists
      if (croppedImageUrl) {
        revokeImageUrl(croppedImageUrl);
      }
      setCroppedImageUrl(undefined);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = async () => {
      // Calculate the actual pixel coordinates from normalized values
      const cropX = boundingBox.left * img.width;
      const cropY = boundingBox.top * img.height;
      const cropWidth = boundingBox.width * img.width;
      const cropHeight = boundingBox.height * img.height;

      // Create a canvas and crop the image
      const canvas = document.createElement('canvas');
      canvas.width = cropWidth;
      canvas.height = cropHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.drawImage(
        img,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        0,
        0,
        cropWidth,
        cropHeight
      );

      // Use the new utility for better performance with large files
      try {
        const newImageUrl = await canvasToImageUrl(canvas, { quality: 0.9 });

        // Cleanup previous image URL if it exists
        if (croppedImageUrl) {
          revokeImageUrl(croppedImageUrl);
        }

        setCroppedImageUrl(newImageUrl);
      } catch (error) {
        console.error('Failed to create cropped image URL:', error);
        setCroppedImageUrl(undefined);
      }
    };

    img.onerror = () => {
      console.error('Failed to load image for cropping:', imageUrl);
      setCroppedImageUrl(undefined);
    };

    img.src = imageUrl;
  }, [imageUrl, boundingBox]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (croppedImageUrl) {
        revokeImageUrl(croppedImageUrl);
      }
    };
  }, [croppedImageUrl]);

  return { croppedImageUrl };
};
